import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { PageInfoBar } from "@components/common/page-info-bar";
import { EditableGroupName } from "@components/group-details/editable-group-name";
import { BaseGroupDetailsProps } from "@custom-types/group-types";
import { useHasUserValidRoleGroupLevel } from "@hooks/access-control/use-has-user-valid-role-group-level";
import { InviteMemberToGroup } from "@pages/group-details/invite-member-to-group";
import { GroupTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { selectedGroupMembersSelector } from "@store/groups/groups-selector";
import { useAppSelector } from "@store/store-helper";
import { getProjectNumbersInGroup } from "@utils/group-utils";
import { useMemo } from "react";

interface Props extends Partial<BaseGroupDetailsProps> {
  /** Flag whether the content should be shown as skeletons because it is still loading */
  isLoading?: boolean;
}

/**
 * Info bar for the group details tabs
 */
export function GroupDetailsInfoBar({
  group,
  isLoading = false,
}: Props): JSX.Element {
  const { groupTabs } = useAppParams();

  const selectedGroupMembers = useAppSelector(selectedGroupMembersSelector);
  const { canEditGroupDetails, canInviteUsersToGroup } =
    useHasUserValidRoleGroupLevel({
      selectedGroup: group ?? null,
    });

  const shouldShowActionButtons = useMemo(() => {
    if (!group || isLoading) {
      return false;
    }

    return groupTabs === GroupTabs.managers && canInviteUsersToGroup;
  }, [canInviteUsersToGroup, group, groupTabs, isLoading]);

  if (!group || isLoading) {
    // Early exit with a loading skeleton if the group is not yet loaded
    return <PageInfoBar isLoading={true} />;
  }

  return (
    <PageInfoBar
      items={[
        {
          columnSize: 2,
          content: (
            <EditableGroupName
              name={group.name}
              isEditable={canEditGroupDetails}
            />
          ),
        },
        {
          columnSize: 6,
          content: (
            <FaroTextField
              label="Members"
              initialValue={selectedGroupMembers.length}
              faroVariant="main"
              fontSize="18px"
            />
          ),
        },
        {
          columnSize: 6,
          content: (
            <FaroTextField
              label="Active projects"
              initialValue={getProjectNumbersInGroup(group)}
              faroVariant="main"
              fontSize="18px"
            />
          ),
        },
      ]}
      action={
        // eslint-disable-next-line react/jsx-no-useless-fragment -- Needed to keep the position of items constant
        shouldShowActionButtons ? <InviteMemberToGroup group={group} /> : <></>
      }
    />
  );
}
